import {
    isFullVariant,
    isPostType,
    isShapedType,
    type GuideSnap,
    type Tbc,
    type TbcPoint,
} from './tbc-types';
import { composeKnobKey } from './stitch-parts';
import { tipGuideTangent, topTipTangent } from './guide-tangent';
import type { Guide, GuideId } from './guide-types';
import { docDefaultParam, type CrochetSizingDefaults } from './sizing-defaults';

/** Rate at which the per-side gap grows with leg length, in world units of
 *  gap per world unit of leg *beyond* TBC_BODY_PADDING_START_WORLD. A "soft
 *  start": the gap stays at 0 until the arm passes the start length, then
 *  ramps up at this slope. This keeps short arms gap-free (they looked
 *  oversized when the gap scaled straight from 0) while long arms still grow a
 *  visible, increasing gap. Applied per side per padPolicyFor(); clamped to
 *  TBC_BODY_PADDING_MAX_WORLD. */
export const TBC_BODY_PADDING_FRACTION = 0.15;

/** Leg length (world units) below which the body gets no gap at all. Above it
 *  the gap ramps up at TBC_BODY_PADDING_FRACTION per extra unit of leg. */
export const TBC_BODY_PADDING_START_WORLD = 8;

/** Ceiling for the per-side pad magnitude (world units) — stops long arms
 *  from growing an oversized gap that eats the body. */
export const TBC_BODY_PADDING_MAX_WORLD = 12;

/** Lower bound for the post-padding visible leg length, in world units.
 *  Padding is proportionally scaled down per leg when the total pad would
 *  otherwise consume more than (legLen − MIN_STUB) — keeps the symbol
 *  visible during the drag-to-create extrusion before a stitch reaches its
 *  natural length, and on legs shorter than 2·MIN where the fraction floor
 *  would otherwise overrun. */
export const TBC_BODY_MIN_STUB_WORLD = 2;

/** Per-side pad magnitude for a leg of the given world length: zero up to the
 *  start length, then FRACTION·(legLen − START) growing to a MAX ceiling.
 *  Which sides actually receive it is decided by padPolicyFor(). */
export function legPadMagnitude(legLen: number): number {
    return Math.min(
        TBC_BODY_PADDING_MAX_WORLD,
        Math.max(
            0,
            TBC_BODY_PADDING_FRACTION *
                (legLen - TBC_BODY_PADDING_START_WORLD),
        ),
    );
}

/** Override flavor for asymmetric rendering. Pending-increase / pending-
 *  decrease previews construct synthetic Tbcs with only one top or base
 *  while the user is hovering for the first endpoint — the handle
 *  count alone (`t.tops.length > 1`, `t.bases.length > 1`) hasn't
 *  caught up yet, but the renderer still needs to draw asymmetric-style
 *  legs. Passing this flavor forces the asymmetric interpretation
 *  regardless of cardinality. `null` falls back to deriving from the
 *  Tbc itself. */
export type AsymmetricRenderOverride = 'increase' | 'decrease' | null;

/** Classify a TBC as an asymmetric increase (1 base / N tops) or decrease
 *  (N bases / 1 top), or null for a regular stitch. Decrease is checked
 *  first; the sizing-handle and padding paths both rely on this precedence
 *  so the handle pair tracks the same leg the renderer draws. */
export function asymmetricKind(t: Tbc, override: AsymmetricRenderOverride): 'increase' | 'decrease' | null {
    if (override != null) return override;
    if (t.bases.length > 1) return 'decrease';
    if (t.tops.length > 1) return 'increase';
    return null;
}

/** Whether THIS leg of a double-crochet draws the top-cap bar. A decrease's
 *  legs all converge on one shared top, so the cap is drawn ONCE — only on the
 *  first leg (legId === firstBaseId) — to read as a single cap at the apex
 *  (matching the dc2tog chart symbol). Increase legs each terminate at their
 *  own top, and symmetric stitches have a single leg, so those always draw the
 *  cap. A null/absent legId (defensive: a decrease with no per-leg id) also
 *  draws the cap so it never vanishes entirely. */
export function dcLegDrawsTopCap(
    kind: 'increase' | 'decrease' | null,
    legId: string | undefined,
    firstBaseId: string | undefined,
): boolean {
    return kind !== 'decrease' || legId == null || legId === firstBaseId;
}

/** Whether THIS leg of a post stitch draws the around-the-post foot hook. An
 *  increase's legs all share one base, so the hook is drawn ONCE — only on the
 *  first leg (legId === firstTopId) — so a single hook wraps the shared post.
 *  Decrease legs each wrap their OWN post (distinct bases), and symmetric
 *  stitches have a single leg, so those always draw the hook. A null/absent
 *  legId (defensive: an increase with no per-leg id) also draws the hook so it
 *  never vanishes entirely. Mirror of {@link dcLegDrawsTopCap}. */
export function postLegDrawsFoot(
    kind: 'increase' | 'decrease' | null,
    legId: string | undefined,
    firstTopId: string | undefined,
): boolean {
    return kind !== 'increase' || legId == null || legId === firstTopId;
}

/** The shared hook axis of a post-stitch increase: the direction from the
 *  single base to the CENTROID of the tops, plus that distance. The increase's
 *  one hook is oriented along this "middle leg" (mirroring how a decrease's
 *  shared apex cap squares on its middle leg via {@link decreaseTopCapBarDir})
 *  and its stretch ratio divides by this distance, so the drawn hook and the
 *  point every leg stems from (its end on this axis — see aimPostIncreaseLeg)
 *  agree on one radius. Null when degenerate (no tops, or the centroid
 *  coincides with the base), letting callers fall back to per-leg frame
 *  fields. */
export function increasePostFootAxis(
    base: TbcPoint,
    tops: ReadonlyArray<{ point: TbcPoint }>,
): { dir: TbcPoint; dist: number } | null {
    const n = tops.length;
    if (n === 0) return null;
    let cx = 0;
    let cy = 0;
    for (const t of tops) {
        cx += t.point.x;
        cy += t.point.y;
    }
    cx /= n;
    cy /= n;
    const dx = cx - base.x;
    const dy = cy - base.y;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return null;
    return { dir: { x: dx / dist, y: dy / dist }, dist };
}

/** Cap-bar direction for a double-crochet decrease whose shared top is NOT
 *  guide-snapped. The single apex cap should sit square across the converging
 *  legs, so it's drawn perpendicular to the "middle" leg — the direction from
 *  the average of all base points (their centroid) to the shared top. Returns
 *  the unit bar direction, or null when degenerate (no bases, or the centroid
 *  coincides with the top), letting the caller fall back to the leg
 *  perpendicular. When the top IS guide-snapped the guide tangent wins instead
 *  (handled by the caller), matching a normal dc. */
export function decreaseTopCapBarDir(bases: { point: TbcPoint }[], top: TbcPoint): TbcPoint | null {
    const n = bases.length;
    if (n === 0) return null;
    let cx = 0;
    let cy = 0;
    for (const b of bases) {
        cx += b.point.x;
        cy += b.point.y;
    }
    cx /= n;
    cy /= n;
    // Middle leg direction: centroid of bases -> shared top.
    const dx = top.x - cx;
    const dy = top.y - cy;
    const len = Math.hypot(dx, dy);
    if (len === 0) return null;
    // Perpendicular to that direction = the cap bar.
    return { x: -dy / len, y: dx / len };
}

/** A resolved cap-bar tangent plus its provenance. `fromGuide: false` marks
 *  the decrease apex-squaring fallback ({@link decreaseTopCapBarDir}) — a
 *  BASELINE the user's advanced capAngle offset rotates from, unlike a real
 *  guide tangent which locks the angle (capAngleLocked / resolveKnobs). */
export type CapTangentInfo = { dir: TbcPoint; fromGuide: boolean };

/** Orientation (unit bar direction) of a double-crochet's top-cap, with
 *  provenance. Follows the top's guide tangent when the top is guide-snapped
 *  (normal-dc behaviour); for an UNSNAPPED decrease it falls back to
 *  {@link decreaseTopCapBarDir} (perpendicular to the middle leg,
 *  `fromGuide: false`) so the single shared apex cap sits square across the
 *  converging legs. Returns null otherwise (unsnapped symmetric / increase),
 *  letting the per-leg renderer fall back to the leg perpendicular.
 *
 *  The renderer (tbc-pixi) and the sizing-handle path (stitch-handles) MUST
 *  both call this so the drawn cap and its drag handle can never diverge. */
export function dcTopCapTangentInfo(t: Tbc, guides: ReadonlyMap<GuideId, Guide> | undefined): CapTangentInfo | null {
    const guideTan = topTipTangent(t.tops, guides);
    if (guideTan != null) return { dir: guideTan, fromGuide: true };
    const fallback = asymmetricKind(t, null) === 'decrease' ? decreaseTopCapBarDir(t.bases, t.tops[0].point) : null;
    return fallback == null ? null : { dir: fallback, fromGuide: false };
}

/** Direction-only view of {@link dcTopCapTangentInfo}. */
export function dcTopCapTangent(t: Tbc, guides: ReadonlyMap<GuideId, Guide> | undefined): TbcPoint | null {
    return dcTopCapTangentInfo(t, guides)?.dir ?? null;
}

/** Per-leg cap-bar orientation for an asymmetric stitch's leg, with
 *  provenance. An INCREASE fans N independent tops from one base, so each
 *  leg's cap follows ITS OWN top's guide tangent (`tipSnap`) — null when that
 *  top is free or there are no guides, which downstream (drawLegCapStitch /
 *  the leg frame) falls back to perpendicular-to-this-arm. Decrease (single
 *  shared apex cap) and symmetric stitches delegate verbatim to
 *  {@link dcTopCapTangentInfo} so their behaviour is unchanged. The renderer
 *  (tbc-pixi) and the sizing-handle path (stitch-handles) MUST both call this
 *  per leg so the drawn cap and its handle never diverge. */
export function legCapTangentInfo(
    t: Tbc,
    kind: 'increase' | 'decrease' | null,
    tipSnap: GuideSnap | null,
    guides: ReadonlyMap<GuideId, Guide> | undefined,
): CapTangentInfo | null {
    if (kind === 'increase') {
        const dir = guides == null ? null : tipGuideTangent(tipSnap, guides);
        return dir == null ? null : { dir, fromGuide: true };
    }
    return dcTopCapTangentInfo(t, guides);
}

/** Direction-only view of {@link legCapTangentInfo}. */
export function legCapTangent(
    t: Tbc,
    kind: 'increase' | 'decrease' | null,
    tipSnap: GuideSnap | null,
    guides: ReadonlyMap<GuideId, Guide> | undefined,
): TbcPoint | null {
    return legCapTangentInfo(t, kind, tipSnap, guides)?.dir ?? null;
}

/** Inset the visible body endpoints inward along the base→top axis. Each
 *  flagged side is inset by legPadMagnitude(legLen) — a fraction of the leg
 *  length, so the gap scales with the arm. When overrideMagnitude is provided,
 *  it is used instead (still bounded by the MIN_STUB clamp). If the resulting
 *  total padding would shrink the leg below TBC_BODY_MIN_STUB_WORLD, both insets
 *  are scaled down proportionally. Returns the *visible* endpoints used for
 *  drawing; the original handle positions are unaffected. */
export function padLegEndpoints(
    base: TbcPoint,
    top: TbcPoint,
    padBaseSide: boolean,
    padTopSide: boolean,
    /** Per-side pad magnitude in world units. A bare `number` applies to both
     *  flagged sides (back-compat with the single-gap model); an object gives
     *  independent `base` / `top` magnitudes. Any side left `undefined` (or the
     *  whole arg omitted) uses the length-aware soft-start `legPadMagnitude`.
     *  Either way the result is bounded by the MIN_STUB clamp below. */
    overrideMagnitude?: number | { base?: number; top?: number },
): { base: TbcPoint; top: TbcPoint } {
    const dx = top.x - base.x;
    const dy = top.y - base.y;
    const legLen = Math.hypot(dx, dy);
    if (legLen === 0) return { base, top };
    const auto = legPadMagnitude(legLen);
    const baseOverride = typeof overrideMagnitude === 'number'
        ? overrideMagnitude
        : overrideMagnitude?.base;
    const topOverride = typeof overrideMagnitude === 'number'
        ? overrideMagnitude
        : overrideMagnitude?.top;
    let pb = padBaseSide ? Math.max(0, baseOverride ?? auto) : 0;
    let pt = padTopSide ? Math.max(0, topOverride ?? auto) : 0;
    const totalPad = pb + pt;
    const maxPad = Math.max(0, legLen - TBC_BODY_MIN_STUB_WORLD);
    if (totalPad > maxPad) {
        const scale = maxPad / totalPad;
        pb *= scale;
        pt *= scale;
    }
    const ux = dx / legLen;
    const uy = dy / legLen;
    return {
        base: { x: base.x + pb * ux, y: base.y + pb * uy },
        top: { x: top.x - pt * ux, y: top.y - pt * uy },
    };
}

/** Per-side body-gap override magnitudes (world units) read from a stitch's
 *  params, for padLegEndpoints. When `legId` is given (an asymmetric leg),
 *  each side first checks the per-leg key `bodyGap<Side>@<legId>`, then falls
 *  back to the per-side param (`bodyGapTop` / `bodyGapBase`), then the legacy
 *  single `bodyGap`, then the document default for the type (clamped to >= 0),
 *  then stays `undefined` so the length-aware auto gap applies to that side.
 *  `??` is used so an explicit per-leg 0 is preserved. Both sides are always
 *  resolved; for an asymmetric `legId` only the padded side (per
 *  `padPolicyFor` — top for an increase leg, base for a decrease) is consumed
 *  downstream, so the other side's value is intentionally inert. */
export function bodyGapOverrideFor(
    t: Tbc, dd: CrochetSizingDefaults | undefined, legId?: string | null,
): { base?: number; top?: number } {
    const legacy = t.params?.bodyGap;
    const side = (key: 'bodyGapTop' | 'bodyGapBase') => {
        const perLeg = legId != null ? t.params?.[composeKnobKey(key, legId)] : undefined;
        const doc = docDefaultParam(dd, t.type, key);
        const docSafe = doc != null ? Math.max(0, doc) : undefined;
        return perLeg ?? t.params?.[key] ?? legacy ?? docSafe;
    };
    return { base: side('bodyGapBase'), top: side('bodyGapTop') };
}

/** Effective pad decision + magnitudes for one leg, in one place: a side is
 *  padded when the type's POLICY flags it (padPolicyFor) OR an EXPLICIT
 *  per-side gap resolves for it — per-leg key, per-stitch key, or document
 *  default. The legacy single `bodyGap` stays policy-gated (it predates
 *  per-side gaps and was defined as "applies to the flagged sides"), so old
 *  documents keep their exact look. This is what lets a post stitch — whose
 *  policy leaves the base unpadded because the hook sits on the base handle —
 *  take a user-adjustable distance from the base (SP-28 follow-up).
 *  Magnitudes on policy sides resolve exactly as bodyGapOverrideFor always
 *  has (perLeg > stitch > legacy > doc > auto). */
export function padSpecFor(
    t: Tbc, dd: CrochetSizingDefaults | undefined, legId?: string | null,
    asymmetricOverride: AsymmetricRenderOverride = null,
): { padBase: boolean; padTop: boolean; override: { base?: number; top?: number } } {
    const policy = padPolicyFor(t, asymmetricOverride);
    const legacy = t.params?.bodyGap;
    const side = (key: 'bodyGapTop' | 'bodyGapBase', policyFlag: boolean) => {
        const perLeg = legId != null ? t.params?.[composeKnobKey(key, legId)] : undefined;
        const doc = docDefaultParam(dd, t.type, key);
        const docSafe = doc != null ? Math.max(0, doc) : undefined;
        const explicit = perLeg ?? t.params?.[key] ?? docSafe;
        return {
            pad: policyFlag || explicit != null,
            // Policy sides keep the historical precedence (legacy beats doc);
            // an explicitly-enabled side never reads the legacy value.
            value: policyFlag ? (perLeg ?? t.params?.[key] ?? legacy ?? docSafe) : explicit,
        };
    };
    const b = side('bodyGapBase', policy.padBase);
    const tp = side('bodyGapTop', policy.padTop);
    return { padBase: b.pad, padTop: tp.pad, override: { base: b.value, top: tp.value } };
}

/** Decide which sides of a TBC's legs get padded. Asymmetric stitches pad
 *  only the unique-end side so the shared handle stays a clean meeting
 *  point. For symmetric stitches, normal-shape variants pad the top side
 *  only (base sits at the symbol's center, not at its end), while
 *  double-crochet and -full shapes pad both. See the design spec for the
 *  full table. `asymmetricOverride` lets preview callers force the
 *  increase/decrease branch even when the synthetic Tbc has only one
 *  top/base placed so far. */
export function padPolicyFor(
    t: Tbc,
    asymmetricOverride: AsymmetricRenderOverride = null,
): { padBase: boolean; padTop: boolean } {
    const kind = asymmetricKind(t, asymmetricOverride);
    if (kind === 'decrease') return { padBase: true, padTop: false };
    if (kind === 'increase') return { padBase: false, padTop: true };
    if (isPostType(t.type)) {                                         // front-post: hook
        return { padBase: false, padTop: true };                     // sits ON the base handle
    }
    if (isShapedType(t.type) && !isFullVariant(t.type)) {              // normal shape 1×1
        return { padBase: false, padTop: true };
    }
    return { padBase: true, padTop: true };                           // double-crochet OR -full 1×1
}

/** Multiplier applied to the `+` crossing-bar half-length on asymmetric legs
 *  (increase or decrease). Keep in sync with the renderer — drawSingleLegShape
 *  imports this so the drawn bar and the sizing handles stay identical. */
export const ASYMMETRIC_CROSSING_BAR_SCALE = 0.5;

/** Offset (in units of b, from the symbol center toward the unique-end tip) at
 *  which the `+` crossing bar sits on an asymmetric leg. Positive for increases
 *  (tip = top), negated by the caller for decreases (tip = base). Shared with
 *  the renderer so the bar and the sizing handles coincide. */
export const ASYMMETRIC_CROSSING_OFFSET = 0.75;
